import {useEffect, useRef} from 'react';
import type {
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
} from '@stripe-internal/data-v1/schema/types';
import useAllocateDepositAddressMutation from 'src/api/graphql/useAllocateDepositAddressMutation';
import {getStore, setValue} from 'src/v2/state/store';

type CurrencyNetwork =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
type AssetCode =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;

type UseAllocateDepositAddressParams = {
  network: CurrencyNetwork | null;
  currency: AssetCode | null;
};

type UseAllocateDepositAddressReturn = {
  address: string;
  isAllocating: boolean;
};

export const useAllocateDepositAddress = (
  params: UseAllocateDepositAddressParams,
): UseAllocateDepositAddressReturn => {
  const {network, currency} = params;
  const store = getStore();
  const addressKey = network && currency ? `${currency}.${network}` : null;

  const cachedAddress =
    store.useSelector((state) =>
      addressKey ? state.directDepositAddresses[addressKey] : undefined,
    ) ?? '';

  // Tracks in-flight requests to prevent duplicate API calls during double renders.
  const pendingRequestRef = useRef<string | null>(null);

  const [allocateDepositAddressMutation, {loading}] =
    useAllocateDepositAddressMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  useEffect(() => {
    if (
      addressKey == null ||
      cachedAddress ||
      pendingRequestRef.current === addressKey
    ) {
      return;
    }

    const allocate = async () => {
      pendingRequestRef.current = addressKey;

      const result = await allocateDepositAddressMutation({
        variables: {
          input: {
            crypto_payin_session: sessionId,
            client_secret: clientSecret,
            network: network!,
            currency: currency!,
          },
        },
      });

      const address =
        result.data
          ?.v1CryptoInternalPayinSessionCryptoPayinSessionAllocateDepositAddress
          ?.deposit_address;

      if (address) {
        setValue((state) => {
          state.directDepositAddresses[addressKey] = address;
        });
      }

      pendingRequestRef.current = null;
    };

    allocate();
  }, [
    addressKey,
    cachedAddress,
    sessionId,
    clientSecret,
    network,
    currency,
    allocateDepositAddressMutation,
  ]);

  return {
    address: cachedAddress,
    isAllocating: loading && !cachedAddress,
  };
};
